import React, { useState, useEffect, useCallback } from 'react';
import { FiUser, FiActivity, FiTrendingUp, FiTarget, FiRefreshCw, FiInfo, FiCheck } from 'react-icons/fi';
import { setDoc, serverTimestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { calculateBMR, calculateTDEE, calculateMacros, calculateCaloriesForGoal, formatNumber } from '../../utils/calculations';
import { activityLevels, goals } from '../../data/mockData';
import './MacroCalculator.css';

const MacroCalculator = () => {
  const { currentUser, userProfile } = useAuth();

  const [formData, setFormData] = useState({
    age: 25,
    gender: 'female',
    weight: 65,
    height: 170,
    activityLevel: 'moderate',
    goal: 'maintenance'
  });

  const [results, setResults] = useState({
    bmr: 0,
    tdee: 0,
    targetCalories: 0,
    macros: { protein: 0, fat: 0, carbs: 0 }
  });

  const [isCalculating, setIsCalculating] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const calculateResults = useCallback(() => {
    setIsCalculating(true);
    
    setTimeout(() => {
      const bmr = calculateBMR(formData.weight, formData.height, formData.age, formData.gender);
      const tdee = calculateTDEE(bmr, formData.activityLevel);
      const targetCalories = calculateCaloriesForGoal(tdee, formData.goal);
      const macros = calculateMacros(targetCalories, formData.weight, formData.goal);

      setResults({
        bmr: Math.round(bmr),
        tdee: Math.round(tdee),
        targetCalories: Math.round(targetCalories),
        macros
      });
      
      setIsCalculating(false);
    }, 500);
  }, [formData]);

  // Load saved data on mount
  useEffect(() => {
    const loadSavedData = async () => {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      try {
        const savedDataDoc = await getDoc(doc(db, 'macroCalculations', currentUser.uid));

        if (savedDataDoc.exists()) {
          const savedData = savedDataDoc.data();

          console.log('Loaded saved data from Firestore:', savedData);

          // Restore form data and results
          if (savedData.formData) {
            console.log('Setting formData to:', savedData.formData);
            setFormData(savedData.formData);
          }

          if (savedData.results) {
            console.log('Setting results to:', savedData.results);
            setResults(savedData.results);
          }

          console.log('Successfully loaded saved macro calculation data');
        } else {
          console.log('No saved macro calculation found for user');
        }
      } catch (error) {
        console.error('Error loading saved data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSavedData();
  }, [currentUser]); // Only run on mount or when currentUser changes

  useEffect(() => {
    if (!isLoading) {
      calculateResults();
    }
  }, [calculateResults, isLoading]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getGoalIcon = (goal) => {
    switch (goal) {
      case 'cutting': return '📉';
      case 'bulking': return '📈';
      case 'maintenance': return '⚖️';
      default: return '⚖️';
    }
  };

  const getGoalColor = (goal) => {
    switch (goal) {
      case 'cutting': return '#ff6b35';
      case 'bulking': return '#4ecdc4';
      case 'maintenance': return '#f4a261';
      default: return '#f4a261';
    }
  };

  const getActivityIcon = (level) => {
    switch (level) {
      case 'sedentary': return '🪑';
      case 'light': return '🚶';
      case 'moderate': return '🏃';
      case 'active': return '🏋️';
      case 'veryActive': return '🤾';
      case 'athlete': return '🏅';
      default: return '🚶';
    }
  };

  const recalculate = () => {
    calculateResults();
  };

  const saveResults = async () => {
    if (!currentUser) {
      alert('Please sign in to save your results');
      return;
    }

    try {
      setIsSaving(true);
      setSaveSuccess(false);

      // Get client's name
      const clientName = userProfile?.displayName || currentUser.displayName || 'Unknown Client';

      // Get trainer's name if trainer is assigned
      let trainerName = null;
      if (userProfile?.trainerId) {
        try {
          const trainerDoc = await getDoc(doc(db, 'users', userProfile.trainerId));
          if (trainerDoc.exists()) {
            trainerName = trainerDoc.data().displayName || 'Unknown Trainer';
          }
        } catch (error) {
          console.error('Error fetching trainer info:', error);
        }
      }

      const calculationData = {
        userId: currentUser.uid,
        clientName: clientName,
        trainerId: userProfile?.trainerId || null,
        trainerName: trainerName,
        formData: {
          age: formData.age,
          gender: formData.gender,
          weight: formData.weight,
          height: formData.height,
          activityLevel: formData.activityLevel,
          goal: formData.goal
        },
        results: {
          bmr: results.bmr,
          tdee: results.tdee,
          targetCalories: results.targetCalories,
          macros: {
            protein: results.macros.protein,
            fat: results.macros.fat,
            carbs: results.macros.carbs
          }
        },
        updatedAt: serverTimestamp()
      };

      // Use userId as document ID to ensure only one calculation per user
      await setDoc(doc(db, 'macroCalculations', currentUser.uid), calculationData);

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      console.log('Macro calculation saved successfully');
    } catch (error) {
      console.error('Error saving macro calculation:', error);
      alert('Failed to save results. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="macro-calculator">
        <div className="calculator-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading your saved results...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="macro-calculator">
      <div className="calculator-container">

        <div className="calculator-content">
          <div className="input-section">
            <div className="form-card">
              <div className="card-header">
                <FiUser className="card-icon" />
                <h3>Personal Information</h3>
              </div>
              <div className="input-row">
                <div className="input-field">
                  <label>
                    Age
                    <FiInfo 
                      className="tooltip-icon" 
                      onMouseEnter={() => setShowTooltip('age')}
                      onMouseLeave={() => setShowTooltip(null)}
                    />
                    {showTooltip === 'age' && (
                      <div className="tooltip">Age affects your metabolic rate</div>
                    )}
                  </label>
                  <div className="input-wrapper">
                    <span className="input-icon">👤</span>
                    <input
                      type="number"
                      value={formData.age}
                      onChange={(e) => handleInputChange('age', parseInt(e.target.value) || 0)}
                      min="15"
                      max="100"
                      placeholder="25"
                    />
                    <span className="unit">years</span>
                  </div>
                </div>
                <div className="input-field">
                  <label>Gender</label>
                  <div className="input-wrapper">
                    <span className="input-icon">⚧</span>
                    <select
                      value={formData.gender}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                    >
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-card">
              <div className="card-header">
                <FiActivity className="card-icon" />
                <h3>Body Measurements</h3>
              </div>
              <div className="input-row">
                <div className="input-field">
                  <label>
                    Weight
                    <FiInfo 
                      className="tooltip-icon" 
                      onMouseEnter={() => setShowTooltip('weight')}
                      onMouseLeave={() => setShowTooltip(null)}
                    />
                    {showTooltip === 'weight' && (
                      <div className="tooltip">Your current body weight</div>
                    )}
                  </label>
                  <div className="input-wrapper">
                    <span className="input-icon">⚖️</span>
                    <input
                      type="number"
                      value={formData.weight}
                      onChange={(e) => handleInputChange('weight', parseFloat(e.target.value) || 0)}
                      min="30"
                      max="300"
                      step="0.1"
                      placeholder="65"
                    />
                    <span className="unit">kg</span>
                  </div>
                </div>
                <div className="input-field">
                  <label>Height</label>
                  <div className="input-wrapper">
                    <span className="input-icon">📏</span>
                    <input
                      type="number"
                      value={formData.height}
                      onChange={(e) => handleInputChange('height', parseInt(e.target.value) || 0)}
                      min="120"
                      max="250"
                      placeholder="170"
                    />
                    <span className="unit">cm</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-card">
              <div className="card-header">
                <FiTrendingUp className="card-icon" />
                <h3>Activity Level</h3>
              </div>
              <div className="activity-cards">
                {activityLevels.map((level) => (
                  <div 
                    key={level.value}
                    className={`activity-card ${formData.activityLevel === level.value ? 'active' : ''}`}
                    onClick={() => handleInputChange('activityLevel', level.value)}
                  >
                    <div className="activity-icon">{getActivityIcon(level.value)}</div>
                    <div className="activity-content">
                      <span className="activity-title">{level.label.split(' ')[0]}</span>
                      <span className="activity-description">{level.label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-card">
              <div className="card-header">
                <FiTarget className="card-icon" />
                <h3>Goal</h3>
              </div>
              <div className="goal-options">
                {goals.map((goal) => (
                  <button
                    key={goal.value}
                    className={`goal-option ${formData.goal === goal.value ? 'active' : ''}`}
                    onClick={() => handleInputChange('goal', goal.value)}
                    style={{ '--goal-color': getGoalColor(goal.value) }}
                  >
                    <span className="goal-icon">{getGoalIcon(goal.value)}</span>
                    <div className="goal-content">
                      <span className="goal-title">{goal.label}</span>
                      <span className="goal-description">{goal.description}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="recalculate-section">
              <button 
                className="recalculate-btn" 
                onClick={recalculate}
                disabled={isCalculating}
              >
                <FiRefreshCw className={`refresh-icon ${isCalculating ? 'spinning' : ''}`} />
                Recalculate
              </button>
            </div>
          </div>

          <div className="results-section">
            <div className={`results-container ${isCalculating ? 'calculating' : ''}`}>
              <h3>Your Personalized Results</h3>
              
              {isCalculating && (
                <div className="loading-skeleton">
                  <div className="skeleton-card"></div>
                  <div className="skeleton-card"></div>
                  <div className="skeleton-card"></div>
                </div>
              )}
              
              {!isCalculating && (() => {
                const maxValue = Math.max(results.bmr, results.tdee, results.targetCalories);
                const bmrPercent = `${(results.bmr / maxValue * 100)}%`;
                const tdeePercent = `${(results.tdee / maxValue * 100)}%`;
                const targetPercent = `${(results.targetCalories / maxValue * 100)}%`;

                return (
                  <div className="results-grid">
                    <div className="result-card bmr">
                      <div className="result-header">
                        <span className="result-icon">🔥</span>
                        <span className="result-label">BMR</span>
                      </div>
                      <div className="result-value">{formatNumber(results.bmr)}</div>
                      <div className="result-unit">calories/day</div>
                      <div className="result-description">Basal Metabolic Rate</div>
                      <div className="result-progress">
                        <div className="progress-ring">
                          <div className="progress-fill" style={{ '--progress': bmrPercent }}></div>
                        </div>
                      </div>
                    </div>

                    <div className="result-card tdee">
                      <div className="result-header">
                        <span className="result-icon">⚡</span>
                        <span className="result-label">TDEE</span>
                      </div>
                      <div className="result-value">{formatNumber(results.tdee)}</div>
                      <div className="result-unit">calories/day</div>
                      <div className="result-description">Total Daily Energy Expenditure</div>
                      <div className="result-progress">
                        <div className="progress-ring">
                          <div className="progress-fill" style={{ '--progress': tdeePercent }}></div>
                        </div>
                      </div>
                    </div>

                    <div className="result-card target">
                      <div className="result-header">
                        <span className="result-icon">🎯</span>
                        <span className="result-label">Target</span>
                      </div>
                      <div className="result-value">{formatNumber(results.targetCalories)}</div>
                      <div className="result-unit">calories/day</div>
                      <div className="result-description">For your goal</div>
                      <div className="result-progress">
                        <div className="progress-ring">
                          <div className="progress-fill" style={{ '--progress': targetPercent }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="macros-section">
                <h4>Daily Macro Targets</h4>
                <div className="macros-visual">
                  <div className="macro-ring">
                    <div className="macro-segments">
                      <div 
                        className="macro-segment protein"
                        style={{ '--percentage': `${(results.macros.protein * 4 / results.targetCalories * 100) || 0}%` }}
                      ></div>
                      <div 
                        className="macro-segment carbs"
                        style={{ '--percentage': `${(results.macros.carbs * 4 / results.targetCalories * 100) || 0}%` }}
                      ></div>
                      <div 
                        className="macro-segment fat"
                        style={{ '--percentage': `${(results.macros.fat * 9 / results.targetCalories * 100) || 0}%` }}
                      ></div>
                    </div>
                    <div className="macro-center">
                      <span className="center-value">{formatNumber(results.targetCalories)}</span>
                      <span className="center-label">calories</span>
                    </div>
                  </div>
                  
                  <div className="macro-breakdown">
                    <div className="macro-item protein">
                      <div className="macro-info">
                        <span className="macro-name">Protein</span>
                        <span className="macro-value">{formatNumber(results.macros.protein)}g</span>
                      </div>
                      <div className="macro-calories">
                        {formatNumber(results.macros.protein * 4)} cal
                      </div>
                    </div>
                    
                    <div className="macro-item carbs">
                      <div className="macro-info">
                        <span className="macro-name">Carbs</span>
                        <span className="macro-value">{formatNumber(results.macros.carbs)}g</span>
                      </div>
                      <div className="macro-calories">
                        {formatNumber(results.macros.carbs * 4)} cal
                      </div>
                    </div>
                    
                    <div className="macro-item fat">
                      <div className="macro-info">
                        <span className="macro-name">Fat</span>
                        <span className="macro-value">{formatNumber(results.macros.fat)}g</span>
                      </div>
                      <div className="macro-calories">
                        {formatNumber(results.macros.fat * 9)} cal
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="formula-info">
                <h4>📋 Calculation Methods</h4>
                <div className="formula-grid">
                  <div className="formula-item">
                    <strong>BMR:</strong> Mifflin-St Jeor Equation
                  </div>
                  <div className="formula-item">
                    <strong>TDEE:</strong> BMR × Activity Multiplier
                  </div>
                  <div className="formula-item">
                    <strong>Protein:</strong> {Math.round(formData.weight * 2.2 * (formData.goal === 'cutting' ? 1.0 : 0.8))}g (0.8-1.0g/lb body weight)
                  </div>
                  <div className="formula-item">
                    <strong>Fat:</strong> {(formData.goal === 'cutting' ? '20' : formData.goal === 'bulking' ? '30' : '25')}% of total calories
                  </div>
                </div>
              </div>
              
              <div className="cta-section">
                <button
                  className="save-results-btn"
                  onClick={saveResults}
                  disabled={isSaving || !currentUser}
                >
                  {isSaving ? (
                    'Saving...'
                  ) : saveSuccess ? (
                    <>
                      <FiCheck style={{ marginRight: '0.5rem' }} />
                      Saved!
                    </>
                  ) : (
                    'Save Results'
                  )}
                </button>
                <button className="start-meal-plan-btn">
                  Start Meal Planning
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MacroCalculator;